import apiClient from '@/api/client';

// ─── Passkeys für die Konsole ────────────────────────────────────────────────
//
// Die Routen sind dieselben wie in der Anwendung – der authRouter hängt an
// beiden APIs. Neu ist nur, dass die Konsole sie überhaupt benutzt.
//
// Ein Passkey gilt für beide Oberflächen, sofern WEBAUTHN_RP_ID die
// gemeinsame Elterndomain ist. Wer sich in der Anwendung einen Schlüssel
// angelegt hat, kann sich damit auch hier anmelden – und umgekehrt. Das ist
// gewollt: es geht um das Konto, nicht um die Oberfläche.

export interface PasskeyInfo {
  id: string;
  deviceName: string | null;
  transports: string | null;
  createdAt: string;
}

/** Kann dieser Browser überhaupt Passkeys? */
export function passkeysMoeglich(): boolean {
  return typeof window !== 'undefined' && Boolean(window.PublicKeyCredential);
}

// ─── Anlegen ─────────────────────────────────────────────────────────────────

export async function passkeyAnlegen(geraetename: string) {
  const { data: optionen } = await apiClient.post('/auth/webauthn/register-options');

  const credential = (await navigator.credentials.create({
    publicKey: erzeugungsOptionen(optionen),
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('Es wurde kein Schlüssel erzeugt.');

  const { data } = await apiClient.post('/auth/webauthn/register-verify', {
    credential: alsRegistrierung(credential),
    deviceName: geraetename,
  });
  return data;
}

// ─── Anmelden ────────────────────────────────────────────────────────────────

export async function passkeyAnmelden(email: string) {
  const { data: optionen } = await apiClient.post('/auth/login/webauthn-options', { email });

  const credential = (await navigator.credentials.get({
    publicKey: abfrageOptionen(optionen),
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('Es wurde kein Schlüssel ausgewählt.');

  const { data } = await apiClient.post('/auth/login/webauthn-verify', {
    email,
    credential: alsAnmeldung(credential),
  });
  return data;
}

// ─── Verwalten ───────────────────────────────────────────────────────────────

export async function passkeysLaden(): Promise<PasskeyInfo[]> {
  const { data } = await apiClient.get<PasskeyInfo[]>('/auth/webauthn/credentials');
  return data;
}

export async function passkeyLoeschen(id: string): Promise<void> {
  await apiClient.delete(`/auth/webauthn/credentials/${id}`);
}

// ─── Umwandlung zwischen JSON und den Binärformen des Browsers ──────────────
//
// Die WebAuthn-Schnittstelle arbeitet mit ArrayBuffern, über die Leitung geht
// base64url. Beides muss an genau den richtigen Stellen ineinander übersetzt
// werden; ein vergessenes Feld führt zu einer Fehlermeldung des Browsers, die
// den Grund nicht nennt.

function ausBase64Url(s: string): ArrayBuffer {
  const base64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const roh = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return bytes.buffer;
}

function nachBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface ErzeugungsJson {
  challenge: string;
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout: number;
  attestation: AttestationConveyancePreference;
  authenticatorSelection: AuthenticatorSelectionCriteria;
  excludeCredentials: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
}

function erzeugungsOptionen(o: ErzeugungsJson): PublicKeyCredentialCreationOptions {
  return {
    ...o,
    challenge: ausBase64Url(o.challenge),
    user: { ...o.user, id: ausBase64Url(o.user.id) },
    excludeCredentials: o.excludeCredentials.map((c) => ({
      id: ausBase64Url(c.id),
      type: 'public-key' as const,
      ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}),
    })),
  };
}

interface AbfrageJson {
  challenge: string;
  rpId: string;
  timeout: number;
  userVerification: UserVerificationRequirement;
  allowCredentials: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
}

function abfrageOptionen(o: AbfrageJson): PublicKeyCredentialRequestOptions {
  return {
    ...o,
    challenge: ausBase64Url(o.challenge),
    allowCredentials: o.allowCredentials.map((c) => ({
      id: ausBase64Url(c.id),
      type: 'public-key' as const,
      ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}),
    })),
  };
}

function alsRegistrierung(c: PublicKeyCredential) {
  const antwort = c.response as AuthenticatorAttestationResponse;
  return {
    id: c.id,
    rawId: nachBase64Url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: nachBase64Url(antwort.clientDataJSON),
      attestationObject: nachBase64Url(antwort.attestationObject),
    },
  };
}

function alsAnmeldung(c: PublicKeyCredential) {
  const antwort = c.response as AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: nachBase64Url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: nachBase64Url(antwort.clientDataJSON),
      authenticatorData: nachBase64Url(antwort.authenticatorData),
      signature: nachBase64Url(antwort.signature),
    },
  };
}
